import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useState, useRef } from 'react';

interface PDFFile {
  id: number;
  name: string;
  url: string;
  uploadDate: string;
  size: string;
}

interface VideoFile {
  id: number;
  name: string;
  url: string;
  uploadDate: string;
  size: string;
}

export default function Crafts() {
  const [pdfFiles, setPdfFiles] = useState<PDFFile[]>([]);
  const [videoFiles, setVideoFiles] = useState<VideoFile[]>([]);
  const [selectedPdf, setSelectedPdf] = useState<PDFFile | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;
    
    Array.from(files).forEach((file) => {
      if (file.type === 'application/pdf') {
        const url = URL.createObjectURL(file);
        const newPdf: PDFFile = {
          id: Date.now() + Math.random(),
          name: file.name,
          url: url,
          uploadDate: new Date().toLocaleDateString('ko-KR'),
          size: formatFileSize(file.size)
        };
        setPdfFiles((prev) => [...prev, newPdf]);
      } else if (file.type.startsWith('video/')) {
        const url = URL.createObjectURL(file);
        const newVideo: VideoFile = {
          id: Date.now() + Math.random(),
          name: file.name,
          url: url,
          uploadDate: new Date().toLocaleDateString('ko-KR'),
          size: formatFileSize(file.size)
        };
        setVideoFiles((prev) => [...prev, newVideo]);
      }
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDeletePdf = (id: number) => {
    const pdfToDelete = pdfFiles.find(pdf => pdf.id === id);
    if (pdfToDelete) {
      URL.revokeObjectURL(pdfToDelete.url);
    }
    setPdfFiles((prev) => prev.filter((pdf) => pdf.id !== id));
    if (selectedPdf?.id === id) {
      setSelectedPdf(null);
    }
  };

  const handleDeleteVideo = (id: number) => {
    const videoToDelete = videoFiles.find(video => video.id === id);
    if (videoToDelete) {
      URL.revokeObjectURL(videoToDelete.url);
    }
    setVideoFiles((prev) => prev.filter((video) => video.id !== id));
    if (selectedVideo?.id === id) {
      setSelectedVideo(null);
    }
  };

  const handleViewPdf = (pdf: PDFFile) => {
    setSelectedPdf(pdf);
  };

  const handleViewVideo = (video: VideoFile) => {
    setSelectedVideo(video);
  };

  const closeViewer = () => {
    setSelectedPdf(null);
  };

  const closeVideoViewer = () => {
    setSelectedVideo(null);
  };

  const panoramaImages = [
    'https://public.readdy.ai/ai/img_res/e10e0275a74621d6fc020adf280237d3.jpg',
    'https://readdy.ai/api/search-image?query=Wide%20panoramic%20view%20of%20recreational%20facility%20with%20wooden%20pergolas%2C%20outdoor%20fitness%20stations%2C%20information%20boards%2C%20landscaped%20gardens%2C%20pathway%20design%2C%20modern%20urban%20park%20infrastructure%2C%20bright%20daylight%2C%20architectural%20photography&width=891&height=630&seq=panorama-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Panoramic%20landscape%20showing%20outdoor%20amenities%20including%20shade%20structures%2C%20sports%20equipment%20installations%2C%20directional%20signage%2C%20green%20technology%20integration%2C%20sustainable%20design%2C%20wide%20format%20photography%2C%20professional%20quality&width=891&height=630&seq=panorama-3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Eco-friendly%20outdoor%20park%20design%20with%20wooden%20pergola%20structures%2C%20children%20playground%20equipment%2C%20nature-inspired%20signage%2C%20lush%20green%20surroundings%2C%20sustainable%20urban%20landscape%2C%20bright%20natural%20lighting%2C%20wide%20panoramic%20view%2C%20professional%20architectural%20photography&width=891&height=630&seq=panorama-4&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Contemporary%20public%20park%20with%20modern%20outdoor%20fitness%20equipment%2C%20decorative%20pergolas%2C%20informational%20kiosks%2C%20manicured%20lawns%2C%20tree-lined%20pathways%2C%20eco-conscious%20design%2C%20panoramic%20wide%20angle%2C%20clean%20minimalist%20aesthetic%2C%20professional%20photography&width=891&height=630&seq=panorama-5&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Green%20design%20technology%20outdoor%20installation%20panorama%2C%20sustainable%20park%20furniture%2C%20exercise%20stations%2C%20shade%20canopies%2C%20natural%20wood%20and%20metal%20materials%2C%20urban%20green%20space%2C%20wide%20landscape%20view%2C%20daylight%20photography%2C%20modern%20eco%20design&width=891&height=630&seq=panorama-6&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Outdoor%20recreational%20space%20with%20pergola%20shelters%2C%20outdoor%20gym%20equipment%2C%20wayfinding%20signs%2C%20native%20plant%20landscaping%2C%20eco-friendly%20materials%2C%20community%20park%20design%2C%20panoramic%20view%2C%20professional%20quality%20photography%2C%20bright%20and%20airy%20atmosphere&width=891&height=630&seq=panorama-7&orientation=landscape'
  ];

  const [currentPanorama, setCurrentPanorama] = useState(0);

  const nextPanorama = () => {
    setCurrentPanorama((prev) => (prev + 1) % panoramaImages.length);
  };

  const prevPanorama = () => {
    setCurrentPanorama((prev) => (prev - 1 + panoramaImages.length) % panoramaImages.length);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="pt-20 md:pt-24 pb-10 md:pb-16 px-4 md:px-8 relative overflow-hidden"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20outdoor%20landscape%20architecture%20with%20wooden%20pergolas%2C%20exercise%20equipment%20installations%2C%20and%20informational%20signage%20in%20a%20beautifully%20designed%20park%20setting%2C%20professional%20photography%2C%20natural%20lighting%2C%20green%20spaces%2C%20contemporary%20design%20elements%2C%20clean%20organized%20layout%2C%20soft%20warm%20tones%2C%20minimalist%20aesthetic&width=1920&height=600&seq=catalog-hero-bg&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-3xl md:text-6xl font-bold text-white mb-4 md:mb-6 drop-shadow-lg">카달로그</h1>
          <p className="text-base md:text-2xl text-white/90 mb-6 md:mb-8 px-2 drop-shadow-md">
            기능과 디자인을 동시에 만족시키는 제품
          </p>
        </div>
      </section>
      
      {/* Panorama Gallery Section */}
      <section className="py-12 md:py-20 px-4 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4 md:mb-6">그린디자인테크 카달로그&브로셔</h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
              파고라, 운동기구, 안내판 등 카달로그를 통해 알아보세요.
            </p>
          </div>
          
          {/* Panorama Gallery */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-12 group">
            <div className="relative" style={{ aspectRatio: '297/210' }}>
              {panoramaImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentPanorama ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Panorama ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              
              {/* Navigation Arrows */}
              <button
                onClick={prevPanorama}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all duration-300 cursor-pointer opacity-0 group-hover:opacity-100 shadow-lg"
              >
                <i className="ri-arrow-left-s-line text-gray-800 text-2xl"></i>
              </button>
              
              <button
                onClick={nextPanorama}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all duration-300 cursor-pointer opacity-0 group-hover:opacity-100 shadow-lg"
              >
                <i className="ri-arrow-right-s-line text-gray-800 text-2xl"></i>
              </button>
              
              {/* Indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {panoramaImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPanorama(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                      index === currentPanorama 
                        ? 'bg-white w-8' 
                        : 'bg-white/50 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Uploaded PDF Files List */}
          {pdfFiles.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <i className="ri-file-pdf-2-line text-red-500"></i>
                PDF 파일
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {pdfFiles.map((pdf) => (
                  <div 
                    key={pdf.id}
                    className="bg-white border border-gray-200 rounded-xl p-4 md:p-5 hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 md:w-14 md:h-14 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-pdf-2-fill text-2xl md:text-3xl text-red-500"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-800 truncate text-sm md:text-base" title={pdf.name}>
                          {pdf.name}
                        </h4>
                        <p className="text-xs md:text-sm text-gray-500 mt-1">
                          {pdf.size} • {pdf.uploadDate}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => handleViewPdf(pdf)}
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                      >
                        <i className="ri-eye-line"></i>
                        보기
                      </button>
                      <button
                        onClick={() => handleDeletePdf(pdf.id)}
                        className="bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-500 py-2 px-3 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Uploaded Video Files List */}
          {videoFiles.length > 0 && (
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <i className="ri-video-line text-teal-500"></i>
                영상 파일
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {videoFiles.map((video) => (
                  <div 
                    key={video.id}
                    className="bg-white border border-gray-200 rounded-xl p-4 md:p-5 hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 md:w-14 md:h-14 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-video-fill text-2xl md:text-3xl text-teal-500"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-800 truncate text-sm md:text-base" title={video.name}>
                          {video.name}
                        </h4>
                        <p className="text-xs md:text-sm text-gray-500 mt-1">
                          {video.size} • {video.uploadDate}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => handleViewVideo(video)}
                        className="flex-1 bg-teal-500 hover:bg-teal-600 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                      >
                        <i className="ri-play-line"></i>
                        재생
                      </button>
                      <button
                        onClick={() => handleDeleteVideo(video.id)}
                        className="bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-500 py-2 px-3 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* PDF Viewer Modal */}
      {selectedPdf && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-3 md:p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <i className="ri-file-pdf-2-fill text-xl text-red-500"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm md:text-base truncate max-w-[200px] md:max-w-[400px]">
                    {selectedPdf.name}
                  </h3>
                  <p className="text-xs text-gray-500">{selectedPdf.size}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={selectedPdf.url}
                  download={selectedPdf.name}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-download-line"></i>
                  <span className="hidden md:inline">다운로드</span>
                </a>
                <button
                  onClick={closeViewer}
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100">
              <iframe
                src={selectedPdf.url}
                className="w-full h-full"
                title={selectedPdf.name}
              />
            </div>
          </div>
        </div>
      )}

      {/* Video Viewer Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-3 md:p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-video-fill text-xl text-teal-500"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm md:text-base truncate max-w-[200px] md:max-w-[400px]">
                    {selectedVideo.name}
                  </h3>
                  <p className="text-xs text-gray-500">{selectedVideo.size}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={selectedVideo.url}
                  download={selectedVideo.name}
                  className="bg-teal-500 hover:bg-teal-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-download-line"></i>
                  <span className="hidden md:inline">다운로드</span>
                </a>
                <button
                  onClick={closeVideoViewer}
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>
            </div>
            <div className="bg-black p-4">
              <video
                src={selectedVideo.url}
                controls
                autoPlay
                className="w-full max-h-[70vh] rounded-lg"
              >
                브라우저가 비디오 재생을 지원하지 않습니다.
              </video>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                <i className="ri-tools-line text-2xl text-emerald-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">제작부터 설치까지</h3>
              <p className="text-sm text-gray-600">환경을 배려한 제작, 사람을 위한 설치</p>
            </div>
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-teal-100 rounded-full flex items-center justify-center">
                <i className="ri-user-star-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">전문성</h3>
              <p className="text-sm text-gray-600">디자인과 기술을 이애하는 전문인력이프로젝트 전반을 체계적으로 관리합니다,</p>
            </div>
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-leaf-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">친환경</h3>
              <p className="text-sm text-gray-600">지속 가능한 소재와 공정으로 환경에 미치는영향을 최소화합니다.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
